import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { useQuickBooks } from '../../../core/hooks/useQuickBooks';
import { LoadingState } from '../../../components/shared/LoadingState';

export const QuickBooksSettings: React.FC = () => {
  const {
    config,
    loading,
    saveConfig,
    initiateOAuthFlow,
    handleOAuthCallback,
    disconnect,
  } = useQuickBooks();

  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      clientId: config?.clientId || '',
      clientSecret: config?.clientSecret || '',
      redirectUri: config?.redirectUri || `${window.location.origin}/settings`,
    },
  });

  const handleOAuth = useCallback(
    async (code: string, state: string, realmId: string | null) => {
      try {
        await handleOAuthCallback(code, state, realmId);
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        alert('Successfully connected to QuickBooks!');
      } catch (error) {
        console.error('OAuth error:', error);
        alert('Failed to connect to QuickBooks. Please try again.');
      }
    },
    [handleOAuthCallback]
  );

  useEffect(() => {
    // Check if returning from OAuth
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const realmId = urlParams.get('realmId');

    if (code && state) {
      handleOAuth(code, state, realmId);
    }
  }, [handleOAuth]);

  useEffect(() => {
    if (config) {
      reset({
        clientId: config.clientId,
        clientSecret: config.clientSecret,
        redirectUri: config.redirectUri,
      });
    }
  }, [config, reset]);

  const onSave = async (data: any) => {
    try {
      setSaving(true);
      await saveConfig({
        ...config,
        ...data,
      });
      alert('QuickBooks configuration saved!');
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = () => {
    try {
      const authUrl = initiateOAuthFlow();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Connect error:', error);
      alert('Failed to initiate OAuth flow');
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm('Are you sure you want to disconnect QuickBooks?')) {
      try {
        await disconnect();
        alert('Disconnected from QuickBooks');
      } catch (error) {
        console.error('Disconnect error:', error);
        alert('Failed to disconnect');
      }
    }
  };

  if (loading) {
    return <LoadingState message="Loading QuickBooks settings..." />;
  }

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      {config?.accessToken && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <CardTitle>Connected to QuickBooks</CardTitle>
              </div>
              <Button variant="destructive" onClick={handleDisconnect}>
                Disconnect
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {config.realmId && (
              <div>
                <p className="text-sm text-muted-foreground">Company ID (Realm ID)</p>
                <p className="font-medium">{config.realmId}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* OAuth Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>OAuth Configuration</CardTitle>
          <CardDescription>
            Configure your QuickBooks OAuth 2.0 credentials.{' '}
            <a
              href="https://developer.intuit.com/app/developer/qbo/docs/develop/authentication-and-authorization"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Learn how to set up OAuth 2.0
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSave)} className="space-y-4">
            <Input
              label="Client ID"
              {...register('clientId', { required: 'Client ID is required' })}
              error={errors.clientId?.message}
            />

            <Input
              label="Client Secret"
              type="password"
              {...register('clientSecret', { required: 'Client Secret is required' })}
              error={errors.clientSecret?.message}
            />

            <Input
              label="Redirect URI"
              {...register('redirectUri', { required: 'Redirect URI is required' })}
              error={errors.redirectUri?.message}
              placeholder="https://your-app.com/settings"
            />

            <div className="flex justify-end gap-2">
              <Button type="submit" variant="outline" loading={saving}>
                Save Configuration
              </Button>
              <Button
                type="button"
                onClick={handleConnect}
                disabled={!config?.clientId || !config?.clientSecret}
              >
                Authenticate with QuickBooks
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Important Note */}
      <Card className="border-yellow-500 bg-yellow-50">
        <CardContent className="pt-6">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> QuickBooks OAuth requires a server-side token exchange.
            Make sure your Firebase Cloud Functions are deployed with the{' '}
            <code className="bg-yellow-100 px-1 py-0.5 rounded">exchangeQuickBooksToken</code>{' '}
            function implemented.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
